import {
    takeEvery,
    all,
    put,
    call,
    select,
    fork
} from 'redux-saga/effects';
import actionFactory from '../../webcommon/header/filterheader/actions';
import * as filterHeaderSelectors from '../../webcommon/header/filterheader/selectors';
import fp from 'lodash/fp';
import { push, replace, LOCATION_CHANGE } from 'connected-react-router';
import urlJoin from 'url-join';
import queryString from 'query-string';
import moment from 'moment';

import * as csaDrilldownApi from '../../api/csaDrilldownApi';
import * as actions from './actions';
import flatten from '../../api/adapters/csaDrilldownFlatten';
import { restructureListByType } from '../../api/adapters/filterAdapters';
import * as selectors from './selectors';
import { searchToFilters, filtersToSearch } from '../../common/urlUtils';
import * as appSelectors from '../../app/selectors';
import * as appActions from '../../app/actions';
import * as routerSelectors from '../../common/router/selectors';
import { getVirtualDirectory } from '../../common/helpermethods/HelperMethods';
import * as configurationApi from '../../api/configurationApi';
import * as systemInfoApi from '../../api/systemInfoApi';
import * as appConstants from '../../common/constants';
import * as appAdapters from '../../api/adapters/systemInfoAdapter';

export const PAGE_ROUTE = urlJoin(getVirtualDirectory(), '/summary/csadrilldown');

const filterHeaderActions = actionFactory('CSADrilldown');

export function* setAvailableCsasByCounterparty(counterparties = []) {
    const potentialCsaValues = yield (select(appSelectors.getCsas));
    const allowedCsas = counterparties && counterparties.length
        ? potentialCsaValues.filter(csa => counterparties.includes(csa.counterparty))
        : potentialCsaValues;

    const restructuredCsas = restructureListByType(allowedCsas);

    yield put(filterHeaderActions.setAvailableListValues('csa', restructuredCsas));

    const activeFilters = yield select(filterHeaderSelectors.getFilters, 'CSADrilldown');
    const csaFilterIndex = fp.findIndex(filter => filter.definition.id === 'csa', activeFilters);
    const csaFilter = activeFilters[csaFilterIndex];

    if ((Array.isArray(csaFilter.value) && csaFilter.value.length) || allowedCsas.length === 1) {
        let newFilterValue;
        if (allowedCsas.length === 1) {
            newFilterValue = [allowedCsas[0].value];
        } else {
            newFilterValue = csaFilter
                .value.filter(csaId => allowedCsas.find(csa => csa.value === csaId));
        }

        const newFilters = fp.set(`[${csaFilterIndex}].value`, newFilterValue, activeFilters);
        const openFilter = yield select(filterHeaderSelectors.getCurrentlyOpenFilter, 'CSADrilldown');
        yield put(filterHeaderActions.setFilters(newFilters));
        if (openFilter) {
            yield put(filterHeaderActions.openFilter(openFilter));
        }
    }
}

export function* filterChanged(forceUpdate, openCsaFilter = false) {
    const initialised = yield select(selectors.getInitialised);
    if (initialised) {
        const currentlyOpenFilter = yield (select(filterHeaderSelectors.getCurrentlyOpenFilter, 'CSADrilldown'));
        if (!currentlyOpenFilter) {
            const filters = yield select(filterHeaderSelectors.getFilters, 'CSADrilldown');
            const filterSearch = filtersToSearch(filters);
            const sp = queryString.parse(filterSearch);
            let force = false;
            if (forceUpdate) {
                sp.force = 'true';
                force = true;
            }
            if (openCsaFilter) {
                sp.openFilter = 'csa';
            }
            const newSearch = `?${queryString.stringify(sp)}`;
            const currentSearch = yield select(routerSelectors.getSearch);
            if (force || !currentSearch) {
                yield put(replace(`${PAGE_ROUTE}${newSearch}`));
            } else if (currentSearch !== newSearch) {
                yield put(push(`${PAGE_ROUTE}${newSearch}`));
            }
        }
    }
}

export function* loadDrilldownFilters(action) {
    const filterDefinitions = yield select(selectors.getFilterDefinitions);
    const defaultFilters = yield select(selectors.getDefaultFilters);
    const filters = searchToFilters('', filterDefinitions, defaultFilters);
    filters.forEach((filter) => {
        if (filter.definition.id === 'runDate') {
            // eslint-disable-next-line no-param-reassign
            filter.value.from = action.date;
        } else if (filter.definition.id === 'csa') {
            // eslint-disable-next-line no-param-reassign
            filter.value = [action.csa];
        } else if (filter.definition.id === 'counterparty') {
            // eslint-disable-next-line no-param-reassign
            filter.value = [action.counterparty];
        }
    });

    const search = filtersToSearch(filters);
    yield put(push(`${PAGE_ROUTE}?${search}`));
}

export function* getCsas() {
    const filters = yield select(filterHeaderSelectors.getFilters, 'CSADrilldown');
    const counterpartyFilter = filters.find(filter => filter.definition.id === 'counterparty');
    yield call(setAvailableCsasByCounterparty, counterpartyFilter.value);
}

export function* getCounterparties() {
    const counterparties = yield select(appSelectors.getCounterparties);
    const restructuredCounterparties = restructureListByType(counterparties);
    yield put(filterHeaderActions.setAvailableListValues('counterparty', restructuredCounterparties));
}

export function* getAvailableValues(action) {
    switch (action.filterId) {
        case 'csa':
            yield call(getCsas);
            break;
        case 'counterparty':
            yield call(getCounterparties);
            break;
        default:
    }
}
export function* handleUpdate(action) {
    const initialised = yield select(selectors.getInitialised);
    if (initialised) {
        let openCsaFilter = false;
        if (action.filterId === 'runDate') {
            try {
                const EodDate = moment(action.value.from).format(appConstants.dateFormat);
                const csas = yield call(systemInfoApi.csaListByEod, EodDate);
                const csaList = appAdapters.createCsaList(csas);
                yield put(appActions.fetchCsaDataSucceeded(csaList));
                if (!csaList.length) {
                    yield put(appActions.fetchCsaDataEmpty('The CSA list was empty for the chosen date'));
                }
                yield call(getCounterparties);
                yield call(getCsas);
            } catch (error) {
                yield put(appActions.fetchCsaDataFailed(error.message));
            }
        }
        if (action.filterId === 'counterparty') {
            yield call(setAvailableCsasByCounterparty, action.value);
            if (action.type === filterHeaderActions.UPDATE_FILTER) {
                const potentialCsaValues = yield (select(appSelectors.getCsas));
                const newCounterparties = action.value || [];
                const allowedCsas = newCounterparties.length
                    ? potentialCsaValues.filter(csa => newCounterparties.includes(csa.counterparty))
                    : potentialCsaValues;
                if (allowedCsas.length !== 1) {
                    openCsaFilter = true;
                }
            }
        } else if (action.filterId === 'csa' && Array.isArray(action.value) && action.value.length) {
            const csas = yield select(appSelectors.getCsas);
            const csa = fp.find(
                csa => csa.value === action.value[0],
                csas
            );
            yield call(setAvailableCsasByCounterparty, csa.counterparty);

            const activeFilters = yield select(filterHeaderSelectors.getFilters, 'CSADrilldown');
            const counterpartyFilterIndex = fp.findIndex(filter => filter.definition.id === 'counterparty', activeFilters);
            const newFilters = csa.counterparty ? fp.set(`[${counterpartyFilterIndex}].value`, [csa.counterparty], activeFilters) : activeFilters;
            yield put(filterHeaderActions.setFilters(newFilters));
        } else if (action.type === filterHeaderActions.RESET_FILTERS) {
            const filters = yield select(filterHeaderSelectors.getFilters, 'CSADrilldown');
            const EodDate = moment(filters[0].value.from).format(appConstants.dateFormat);
            const csas = yield call(systemInfoApi.csaListByEod, EodDate);
            const csaList = appAdapters.createCsaList(csas);
            yield put(appActions.fetchCsaDataSucceeded(csaList));
            if (!csaList.length) {
                yield put(appActions.fetchCsaDataEmpty('The CSA list was empty for the chosen date'));
            }
            yield call(getCounterparties);
            yield call(getCsas);
            const counterpartyFilterValue = fp.flow(
                fp.find(filter => fp.get('definition.id', filter) === 'counterparty'),
                fp.get('value')
            )(filters);
            yield call(setAvailableCsasByCounterparty, counterpartyFilterValue);
        }

        yield call(filterChanged, false, openCsaFilter);
    }
}

export function* loadDefaultConfiguration(api) {
    try {
        const config = yield call(api.getConfiguration, 'CSADrilldown');
        if (config && config.columns) {
            yield put(actions.setDefaultConfig(config.columns));
        }
    } catch (error) {
        yield put(actions.fetchDefaultConfigFailed(error.message));
    }
}

export function* csaDrilldownsLoaded(configApi) {
    try {
        const initialised = yield (select(selectors.getInitialised));
        if (!initialised) {
            yield fork(loadDefaultConfiguration, configApi);
            yield put(actions.setLoading(true));
            const filterDefinitions = yield select(selectors.getFilterDefinitions);
            yield put(filterHeaderActions.setFilterDefinitions(filterDefinitions));
            const defaultFilters = yield select(selectors.getDefaultFilters);
            const availableEOD = yield select(appSelectors.getAvailableEOD);
            const singleDate = fp.set('to', null)(availableEOD);
            const filters = fp.set('[0].value', singleDate)(defaultFilters);
            const csas = yield call(systemInfoApi.csaListByEod, availableEOD.from);
            const csaList = appAdapters.createCsaList(csas);
            yield put(appActions.fetchCsaDataSucceeded(csaList));
            if (!csaList.length) {
                yield put(appActions.fetchCsaDataEmpty('The CSA list was empty for the chosen date'));
            }
            yield put(filterHeaderActions.setDefaultFilters(filters));
            const search = yield select(routerSelectors.getSearch);
            if (!search) {
                yield put(filterHeaderActions.resetFilters());
            } else {
                const filters = searchToFilters(search, filterDefinitions, defaultFilters);
                yield put(filterHeaderActions.setFilters(filters));
            }
            yield call(getCounterparties);
            yield put(actions.setInitialised(true));
            yield call(filterChanged, true);
        }
    } finally {
        yield put(actions.setLoading(false));
    }
}

export function* handleLocationChangedWithoutQuerystring() {
    const initialised = yield (select(selectors.getInitialised));
    if (initialised) {
        yield call(filterChanged, false);
    }
}

export function* refreshCsaDrilldown(api){
    try{
        const filters = yield select(filterHeaderSelectors.getFilters, 'CSADrilldown');
        const requestedServerFilters = fp.flow(
            fp.filter(filter => filter.value && filter.definition.server),
            fp.sortBy(filter => filter.definition.id),
            JSON.stringify
        )(filters);
        yield put(actions.setLoading(true));
        const data = yield call(api.csaDrilldowns, filters);
        yield put(actions.fetchCsaDrilldownSucceeded(
            flatten(data),
            requestedServerFilters
            ));
    }
    catch (error){
        yield put(actions.fetchCsaDrilldownFailed(error.message));
    } finally {
        yield put(actions.setLoading(false));
    }
}

export function* handleLocationChanged(api, action) {
    const initialised = yield (select(selectors.getInitialised));
    if (initialised) {
        try {
            const filterDefinitions = yield select(selectors.getFilterDefinitions);
            const defaultFilters = yield select(selectors.getDefaultFilters);
            const filters = searchToFilters(
                action.payload.location.search,
                filterDefinitions,
                defaultFilters
            );
            yield put(filterHeaderActions.setFilters(filters));
            const params = queryString.parse(action.payload.location.search);
            const force = params.force === 'true';

            const requestedServerFilters = fp.flow(
                fp.filter(filter => filter.value && filter.definition.server),
                fp.filter(filter => filter.definition.id !== 'counterparty'),
                fp.sortBy(filter => filter.definition.id),
                JSON.stringify
            )(filters);
            const currentServerFilters = yield select(selectors.getCurrentServerFilters);
            if (force || (currentServerFilters !== requestedServerFilters)) {
                yield put(actions.setLoading(true));
                const data = yield call(api.csaDrilldowns, filters);
                yield put(actions.fetchCsaDrilldownSucceeded(
                    flatten(data),
                    requestedServerFilters
                ));
            }
            if (action && action.payload && action.payload.action === 'PUSH') {
                if (params.openFilter === 'csa') {
                    yield put(filterHeaderActions.openFilter('csa'));
                }
            }
        } catch (error) {
            yield put(actions.fetchCsaDrilldownFailed(error.message));
        } finally {
            yield put(actions.setLoading(false));
        }
    }
}

export function* openFilter(filter) {
    if (!filter.filterId) {
        yield call(filterChanged);
    }
}

const testLocation = emptySearch => (action) => {
    if (action.type !== LOCATION_CHANGE) {
        return false;
    }
    if (action.payload.location.pathname.toLowerCase() === PAGE_ROUTE.toLowerCase()) {
        return !!action.payload.location.search !== emptySearch;
    }
    return false;
};

export default function* rootSaga() {
    yield all([
        takeEvery(actions.CSADRILLDOWN_LOADED, csaDrilldownsLoaded, configurationApi),
        takeEvery([
            filterHeaderActions.UPDATE_FILTER,
            filterHeaderActions.REMOVE_FILTER,
            filterHeaderActions.RESET_FILTERS
        ], handleUpdate),
        takeEvery([
            actions.REFRESH_DATA
        ], filterChanged, true),
        takeEvery(
            filterHeaderActions.FETCH_AVAILABLE_LIST_VALUES,
            getAvailableValues,
        ),
        takeEvery(actions.SET_DRILLDOWN_FILTERS, loadDrilldownFilters),
        takeEvery(
            testLocation(false),
            handleLocationChanged,
            csaDrilldownApi
        ),
        takeEvery(
            testLocation(true),
            handleLocationChangedWithoutQuerystring,
            csaDrilldownApi
        ),
        takeEvery(filterHeaderActions.OPEN_FILTER, openFilter),
        takeEvery(actions.REFRESH_CSA_DRILLDOWN, refreshCsaDrilldown, csaDrilldownApi)
    ]);
}
